// src/components/AgentSidebar.tsx
// A13 — Contracts Administrator (CA) Agent — Wave 2
// Live AI panel powered by Anthropic API, appears on all CA module pages.
// Reads current platform data, surfaces urgent/warning/advisory items.

import { useState, useEffect, useCallback } from 'react'
import { Bot, RefreshCw, ChevronDown, ChevronUp, AlertTriangle, Clock, Info, CheckCircle, Zap, ExternalLink } from 'lucide-react'

// ── Types ─────────────────────────────────────────────────────────

interface AgentAlert {
  id: string
  title: string
  detail: string
  action: string
  clause?: string
  ref?: string          // variation number, contract number, etc.
}

interface AgentOutput {
  urgent:   AgentAlert[]
  warning:  AgentAlert[]
  advisory: AgentAlert[]
  summary:  string
}

export type AgentSidebarPage = 'variations' | 'contracts' | 'live-budget' | 'claims' | 'subcontracts'

export interface AgentSidebarContext {
  page: AgentSidebarPage
  contracts?: any[]
  variations?: any[]
  claims?: any[]
  subcontracts?: any[]
  budget?: Record<string, any>
  platformDate?: string   // ISO date — the platform "today" (2026-06-17 per mock data)
}

// ── CA Agent system prompt ─────────────────────────────────────────
// Based on A13 profile in CA Agent Phased Build Plan Section 6

const CA_AGENT_SYSTEM_PROMPT = `You are A13 — the Contracts Administrator (CA) Agent for TrafficProcure MCBCE-K, the operations platform for MCB Civil Electrical Pty Ltd, a Queensland civil and electrical contractor.

ACADEMIC EQUIVALENCE: Bachelor of Construction Management (AQF Level 7) with post-graduate Certificate in Contract Administration. Aligned with AIB and Society of Construction Law Australia competency frameworks. 3–5 years Queensland head contract delivery experience across AS4000, AS2124, and NEC4 forms of contract.

PRIMARY KNOWLEDGE AUTHORITIES:
- AS 4000-1997 General Conditions of Contract: Clause 40 (variations), Clause 41.1 (variation notice time bar — 14 calendar days from direction), Clause 44.4 (payment schedule — 15 business days from claim submission)
- AS 2124-1992 General Conditions of Contract: Clause 40.1 (variation notice — 28 calendar days), Clause 44 (payment schedule — 14 business days)
- NEC4 Engineering and Construction Contract: Clause 61.3 (compensation event notification — 8 weeks from becoming aware)
- Building Industry Fairness (Security of Payment) Act 2017 (Qld): Parts 2 and 3 — payment claims, payment schedules, adjudication. Superintendent must issue payment schedule within 15 business days.
- Building and Construction Industry Payments Act 2004 (Qld): superseded by BIF Act but relevant for transitional contracts
- QBCC Act 1991: Section 67AA — subcontracting disclosure obligation above $150,000 threshold

ALERT THRESHOLDS:
- Variation notice deadline: WARN at 7 days remaining, URGENT at 3 days remaining, MISSED if overdue
- Bond expiry: WARN at 90 days remaining, URGENT at 30 days remaining
- PC date approach: WARN at 30 days remaining, URGENT at 14 days remaining
- Superintendent payment schedule: WARN at 10 business days, URGENT at 14 business days
- Budget utilisation by cost code: WARN at 80% committed, URGENT at 90% committed

YOUR BEHAVIOUR:
- Be specific — always cite exact clause numbers, exact day counts, exact dollar amounts
- Reference specific record numbers (VR-001, con1, etc.)
- Do not be generic or vague — "you should review your variations" is useless, "VR-002 notice expires 24 Jun under AS4000 Cl.41.1 — 7 days remaining, draft notice now" is correct
- Prioritise by contractual risk (time-barred claims = highest risk)
- If a notice deadline is MISSED, state clearly that the right to claim may be time-barred and recommend seeking legal advice from A07

PLATFORM DATE: Use the platformDate value provided in the context as "today". All day calculations must use this date.

CLIENT COST CODE VALIDATION (TMR, Council, and utility contracts):
When reviewing progress claims, check whether each claim line item carries an MRS group (e.g. MRS93) and Work Item ID (e.g. 93001L). If any line item is missing these fields:
- Flag as URGENT: "Claim line [description] has no MRS cost code — TMR will reject this line and require resubmission, delaying payment."
- Action: "Assign correct MRS code from client library before submitting claim."
If the client cost code library status is PENDING_IMPORT or ILLUSTRATIVE, include an ADVISORY item:
- Title: "TMR cost code library not yet imported"
- Detail: "The TMR Estimate Import Template has not been loaded. A12 and A13 cannot auto-validate claim lines or pre-fill BOQ codes. Obtain template from your TMR project manager or estimating.support@tmr.qld.gov.au (PCEM 9th Ed Annexure C)."
- Action: "Import TMR Estimate Import Template via Cost Codes admin page."
For variation pricing on TMR contracts, note whether each variation scope item maps to an existing MRS code or requires a 'rate to be agreed' item. If scope falls outside the loaded library, flag as ADVISORY.

OUTPUT FORMAT: Respond ONLY with a JSON object — no preamble, no markdown fences, no explanation outside the JSON. Use this exact structure:
{
  "urgent": [{"id":"u1","title":"...","detail":"...","action":"...","clause":"...","ref":"..."}],
  "warning": [{"id":"w1","title":"...","detail":"...","action":"...","clause":"...","ref":"..."}],
  "advisory": [{"id":"a1","title":"...","detail":"...","action":"...","ref":"..."}],
  "summary": "One sentence overall assessment of the current commercial risk position."
}

Keep each item concise: title max 8 words, detail max 40 words, action max 20 words. You may return empty arrays if there are no items in that category.`

// ── Anthropic API call ──────────────────────────────────────────────

async function callCAAgent(context: AgentSidebarContext): Promise<AgentOutput> {
  const userMessage = `Review the following platform data and identify items requiring attention.

Platform date (today): ${context.platformDate ?? '2026-06-17'}

${context.contracts?.length ? `PROJECT CONTRACTS:\n${JSON.stringify(context.contracts, null, 2)}\n` : ''}
${context.variations?.length ? `VARIATIONS:\n${JSON.stringify(context.variations, null, 2)}\n` : ''}
${context.claims?.length ? `PROGRESS CLAIMS:\n${JSON.stringify(context.claims, null, 2)}\n` : ''}
${context.subcontracts?.length ? `SUBCONTRACTS:\n${JSON.stringify(context.subcontracts, null, 2)}\n` : ''}
${context.budget ? `BUDGET STATUS:\n${JSON.stringify(context.budget, null, 2)}\n` : ''}

Identify urgent items, warnings, and advisory items based on your knowledge authorities and the alert thresholds defined in your system prompt. Return only the JSON object.`

  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-6',
      max_tokens: 1000,
      system: CA_AGENT_SYSTEM_PROMPT,
      messages: [{ role: 'user', content: userMessage }],
    }),
  })

  if (!response.ok) throw new Error(`API error ${response.status}`)
  const data = await response.json()
  const text = data.content?.find((b: any) => b.type === 'text')?.text ?? '{}'
  // Strip any accidental markdown fences
  const clean = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim()
  return JSON.parse(clean) as AgentOutput
}

// ── Sub-components ─────────────────────────────────────────────────

function AlertCard({
  alert,
  level,
}: {
  alert: AgentAlert
  level: 'urgent' | 'warning' | 'advisory'
}) {
  const styles = {
    urgent:   { bg: 'bg-red-50',    border: 'border-red-200',   icon: AlertTriangle, iconCls: 'text-red-600',  titleCls: 'text-red-900',  dotCls: 'bg-red-500'  },
    warning:  { bg: 'bg-amber-50',  border: 'border-amber-200', icon: Clock,         iconCls: 'text-amber-600',titleCls: 'text-amber-900',dotCls: 'bg-amber-500'},
    advisory: { bg: 'bg-blue-50',   border: 'border-blue-100',  icon: Info,          iconCls: 'text-blue-500', titleCls: 'text-blue-900', dotCls: 'bg-blue-400' },
  }[level]
  const Icon = styles.icon

  return (
    <div className={`rounded-xl border ${styles.border} ${styles.bg} p-3 space-y-1.5`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${styles.iconCls}`} />
        <div className="flex-1 min-w-0">
          <div className={`text-xs font-semibold leading-snug ${styles.titleCls}`}>
            {alert.title}
            {alert.ref && <span className="ml-1.5 font-mono font-normal opacity-70">{alert.ref}</span>}
          </div>
          {alert.clause && (
            <div className="text-[10px] text-gray-500 font-medium mt-0.5">{alert.clause}</div>
          )}
        </div>
      </div>
      <p className="text-[11px] text-gray-700 leading-relaxed pl-5">{alert.detail}</p>
      <div className={`flex items-center gap-1 pl-5 text-[10px] font-semibold ${styles.titleCls}`}>
        <CheckCircle className="w-3 h-3 flex-shrink-0" />
        {alert.action}
      </div>
    </div>
  )
}

// ── Main component ──────────────────────────────────────────────────

export function AgentSidebar({ context }: { context: AgentSidebarContext }) {
  const [output, setOutput]     = useState<AgentOutput | null>(null)
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)  // collapsed by default — click to expand
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const PAGE_LABELS: Record<AgentSidebarPage, string> = {
    'variations':   'Variations',
    'contracts':    'Project Contracts',
    'live-budget':  'Live Budget',
    'claims':       'Progress Claims',
    'subcontracts': 'Subcontracts',
  }

  const runAgent = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await callCAAgent(context)
      setOutput(result)
      setLastUpdated(new Date())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'CA Agent unavailable')
    } finally {
      setLoading(false)
    }
  }, [context.page]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { runAgent() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const urgentCount  = output?.urgent.length  ?? 0
  const warningCount = output?.warning.length ?? 0
  const totalCount   = urgentCount + warningCount + (output?.advisory.length ?? 0)

  // ── Collapsed summary bar ──────────────────────────────────────
  if (!expanded) {
    return (
      <div
        onClick={() => setExpanded(true)}
        className="mb-5 rounded-xl border border-gray-200 bg-white px-4 py-2.5 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-1.5">
          <Bot className="w-4 h-4 text-[#2E75B6]" />
          <span className="text-xs font-semibold text-gray-700">A13 CA Agent</span>
          <span className="text-[10px] text-gray-400">· Wave 2</span>
        </div>
        {loading ? (
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <RefreshCw className="w-3 h-3 animate-spin" />Reviewing…
          </span>
        ) : error ? (
          <span className="text-xs text-red-500">Unavailable</span>
        ) : (
          <div className="flex items-center gap-2">
            {urgentCount > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {urgentCount} urgent
              </span>
            )}
            {warningCount > 0 && (
              <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {warningCount} warning
              </span>
            )}
            {totalCount === 0 && (
              <span className="text-[10px] text-green-600 font-medium">✓ No immediate actions</span>
            )}
          </div>
        )}
        <span className="text-xs text-gray-400 ml-auto flex items-center gap-1">
          Expand <ChevronDown className="w-3.5 h-3.5" />
        </span>
      </div>
    )
  }

  // ── Expanded panel ─────────────────────────────────────────────
  return (
    <div className="mb-5 rounded-xl border border-[#2E75B6]/20 bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#1F3864] text-white">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#2E75B6]/40 flex items-center justify-center flex-shrink-0">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-bold leading-none">A13 — Contracts Administrator</div>
            <div className="text-[10px] text-blue-300 mt-0.5">Wave 2 · AQF 7-8 · {PAGE_LABELS[context.page]}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && !loading && (
            <span className="text-[10px] text-blue-300">
              Updated {lastUpdated.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={() => runAgent()}
            disabled={loading}
            className="p-1.5 text-blue-300 hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-40 transition-colors"
            title="Refresh CA Agent analysis"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setExpanded(false)}
            className="p-1.5 text-blue-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center gap-3 py-4">
            <div className="w-8 h-8 rounded-full bg-[#1F3864]/10 flex items-center justify-center flex-shrink-0">
              <RefreshCw className="w-4 h-4 text-[#2E75B6] animate-spin" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700">A13 is reviewing current platform data…</div>
              <div className="text-xs text-gray-400 mt-0.5">
                Checking variation notice deadlines · PC dates · bonds · budget exposure
              </div>
            </div>
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-red-800">CA Agent temporarily unavailable</div>
              <div className="text-xs text-red-600 mt-0.5">{error}</div>
              <button onClick={() => runAgent()} className="mt-2 text-xs text-red-700 font-semibold hover:underline">
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Output */}
        {!loading && !error && output && (
          <>
            {/* Summary */}
            {output.summary && (
              <div className="flex items-start gap-2 bg-[#1F3864]/5 border border-[#1F3864]/10 rounded-xl px-3 py-2.5">
                <Zap className="w-3.5 h-3.5 text-[#2E75B6] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-700 leading-relaxed">{output.summary}</p>
              </div>
            )}

            {/* Urgent */}
            {output.urgent.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-[10px] font-bold text-red-700 uppercase tracking-wider">
                    Urgent — act today ({output.urgent.length})
                  </span>
                </div>
                <div className="space-y-2">
                  {output.urgent.map(a => <AlertCard key={a.id} alert={a} level="urgent" />)}
                </div>
              </div>
            )}

            {/* Warning */}
            {output.warning.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">
                    Warning — this week ({output.warning.length})
                  </span>
                </div>
                <div className="space-y-2">
                  {output.warning.map(a => <AlertCard key={a.id} alert={a} level="warning" />)}
                </div>
              </div>
            )}

            {/* Advisory */}
            {output.advisory.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="w-2 h-2 rounded-full bg-blue-400" />
                  <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">
                    Advisory ({output.advisory.length})
                  </span>
                </div>
                <div className="space-y-2">
                  {output.advisory.map(a => <AlertCard key={a.id} alert={a} level="advisory" />)}
                </div>
              </div>
            )}

            {/* All clear */}
            {output.urgent.length === 0 && output.warning.length === 0 && output.advisory.length === 0 && (
              <div className="flex items-center gap-3 py-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700">No immediate actions required</div>
                  <div className="text-xs text-gray-400 mt-0.5">All contract obligations are on track</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2 border-t bg-gray-50 text-[10px] text-gray-400">
        <span>A13 · Level 2 Assisted · All outputs require PM review before action</span>
        <span className="flex items-center gap-1">
          <ExternalLink className="w-2.5 h-2.5" />
          Wave 2 — Q4 2026
        </span>
      </div>
    </div>
  )
}
